import type { Diet, Member } from './household';
import { productOf } from './price-book';
import type { Ingredient } from './recipe';
import type { DietTag, Product } from './types';

const EXCLUDED_BY_DIET: Readonly<Record<Diet, readonly DietTag[]>> = {
  omnivore: [],
  pescatarian: ['meat', 'pork'],
  vegetarian: ['meat', 'pork', 'fish', 'seafood'],
  vegan: ['meat', 'pork', 'fish', 'seafood', 'dairy', 'egg', 'honey'],
};

/**
 * DE: Merkmale, die eine Ernährungsweise ausschließt.
 * EN: Attributes that a diet excludes.
 */
export function excludedTagsFor(diet: Diet): DietTag[] {
  return [...EXCLUDED_BY_DIET[diet]];
}

/**
 * DE: Darf die Person dieses Produkt essen (Ernährungsweise, Ausschlüsse, Abneigungen)?
 * EN: May the member eat this product (diet, exclusions, dislikes)?
 */
export function canEat(member: Member, product: Product): boolean {
  if (member.dislikedProductIds.includes(product.id)) return false;
  const excluded = [...excludedTagsFor(member.diet), ...member.excludedTags];
  return !(product.tags ?? []).some((tag) => excluded.includes(tag));
}

/**
 * DE: Ein gemeinsames Rezept ist erlaubt, wenn alle Personen alle Zutaten essen dürfen.
 * EN: A shared recipe is allowed when every member may eat every ingredient.
 */
export function isRecipeAllowed(
  recipe: { ingredients: readonly Ingredient[] },
  members: readonly Member[],
  catalog: ReadonlyMap<string, Product>,
): boolean {
  return recipe.ingredients.every((ingredient) => {
    const product = productOf(catalog, ingredient.productId);
    return members.every((member) => canEat(member, product));
  });
}

/**
 * DE: Fleischbeilage nur aus Fleischprodukten und nur für Personen, die sie essen dürfen.
 * EN: Meat add-ons only from meat products and only for members allowed to eat them.
 */
export function canTakeMeatAddOn(member: Member, product: Product): boolean {
  return (product.tags ?? []).includes('meat') && canEat(member, product);
}
